import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, setHours, setMinutes } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, Clock, X } from 'lucide-react';

const PickerWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const InputTrigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-1px);
    box-shadow: 
      0 10px 15px -3px rgba(0, 0, 0, 0.2),
      0 4px 6px -2px rgba(0, 0, 0, 0.1);
  }

  ${props => props.$isOpen && `
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(10, 132, 255, 0.5);
    box-shadow: 0 0 0 4px rgba(10, 132, 255, 0.15);
  `}
`;

const ValueText = styled.span`
  font-size: 15px;
  color: ${props => props.$hasValue ? '#f5f5f7' : 'rgba(255, 255, 255, 0.5)'};
  font-weight: 500;
  letter-spacing: -0.01em;
`;

const IconWrapper = styled.div`
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(4px);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PickerContainer = styled(motion.div)`
  width: 360px;
  background: rgba(30, 30, 35, 0.85);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 24px;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const MonthNav = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-weight: 600;
  font-size: 17px;
  color: #f5f5f7;
`;

const NavButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #f5f5f7;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  text-align: center;
`;

const WeekDay = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 600;
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const DayButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: transparent;
  font-size: 15px;
  color: ${props => props.$isSameMonth ? '#f5f5f7' : 'rgba(255, 255, 255, 0.2)'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  ${props => props.$isToday && `
    color: #0a84ff;
    font-weight: 600;
    &::after {
      content: '';
      position: absolute;
      bottom: 4px;
      width: 4px;
      height: 4px;
      background: #0a84ff;
      border-radius: 50%;
    }
  `}

  ${props => props.$isSelected && `
    background: #0a84ff !important;
    color: white !important;
    font-weight: 600;
    box-shadow: 0 4px 10px rgba(10, 132, 255, 0.3);
    &::after {
      display: none;
    }
  `}
`;

const TimeSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const TimeLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
`;

const TimeInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.05);
  padding: 8px 12px;
  border-radius: 12px;
`;

const TimeInput = styled.input`
  background: transparent;
  border: none;
  width: 24px;
  font-size: 17px;
  font-weight: 600;
  color: #f5f5f7;
  text-align: center;
  padding: 0;

  &:focus {
    outline: none;
    color: #0a84ff;
  }

  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const TimeSeparator = styled.span`
  color: rgba(255, 255, 255, 0.3);
  font-weight: 600;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: #0a84ff;
    color: white;
    box-shadow: 0 4px 12px rgba(10, 132, 255, 0.25);
    
    &:hover {
      background: #0070e0;
      transform: translateY(-1px);
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: #f5f5f7;
    
    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  `}
`;

const GlassyDateTimePicker = ({ value, onChange, placeholder = "Select date & time", mode = 'datetime' }) => {
  const parseInputDate = useCallback((val) => {
    if (!val) return null;
    // Explicit time-only string
    if (mode === 'time' && typeof val === 'string' && val.includes(':') && !val.includes('T')) {
      return new Date();
    }
    // If an ISO string with trailing Z is provided, strip the Z to avoid timezone shifts
    if (typeof val === 'string' && val.endsWith('Z')) {
      val = val.slice(0, -1);
    }
    // Plain date string (YYYY-MM-DD) should be parsed as local date, not UTC
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const [y, m, d] = val.split('-').map((n) => parseInt(n, 10));
      return new Date(y, m - 1, d);
    }
    return new Date(val);
  }, [mode]);

  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const initialDate = parseInputDate(value) || (mode === 'time' ? new Date() : null);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [time, setTime] = useState({
    hours: initialDate ? initialDate.getHours() : 12,
    minutes: initialDate ? initialDate.getMinutes() : 0
  });

  // Update internal state when prop changes
  useEffect(() => {
    if (value) {
      const date = parseInputDate(value);
      if (!date || isNaN(date.getTime())) return;

      if (mode === 'time' && typeof value === 'string' && value.includes(':') && !value.includes('T')) {
        const [h, m] = value.split(':');
        setTime({ hours: parseInt(h, 10), minutes: parseInt(m, 10) });
        setSelectedDate(new Date());
      } else {
        setSelectedDate(date);
        setCurrentMonth(date);
        setTime({ hours: date.getHours(), minutes: date.getMinutes() });
      }
    }
  }, [value, mode, parseInputDate]);

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  // Add padding days for start of month
  const startDay = startOfMonth(currentMonth).getDay();
  const paddingDays = Array(startDay).fill(null);

  const handleDateClick = (day) => {
    setSelectedDate(day);
    if (mode === 'date') {
      // For date mode, we can confirm immediately or wait for user to click confirm
      // Let's wait for confirm to be consistent, or maybe auto-close?
      // Let's keep it consistent with Confirm button for now.
    }
  };

  const handleTimeChange = (field, val) => {
    let num = parseInt(val);
    if (isNaN(num)) return;

    if (field === 'hours') {
      if (num < 0) num = 23;
      if (num > 23) num = 0;
    } else {
      if (num < 0) num = 59;
      if (num > 59) num = 0;
    }

    setTime(prev => ({ ...prev, [field]: num }));
  };

  const handleConfirm = () => {
    if (mode === 'time') {
      const timeStr = `${time.hours.toString().padStart(2, '0')}:${time.minutes.toString().padStart(2, '0')}`;
      // If the parent expects a full date object even for time mode, we might need to adjust.
      // But usually time inputs expect "HH:mm".
      // Let's return "HH:mm" for time mode.
      onChange(timeStr);
      setIsOpen(false);
      return;
    }

    if (selectedDate) {
      let finalDate = new Date(selectedDate);
      if (mode === 'datetime') {
        finalDate = setMinutes(setHours(finalDate, time.hours), time.minutes);
        // Return a local datetime string without timezone offset to avoid shifts
        onChange(format(finalDate, "yyyy-MM-dd'T'HH:mm"));
      } else if (mode === 'date') {
        // Return YYYY-MM-DD
        onChange(format(finalDate, 'yyyy-MM-dd'));
      }
      setIsOpen(false);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setSelectedDate(null);
  };

  const formatDisplayValue = () => {
    if (!value) return placeholder;

    if (mode === 'time') {
      if (typeof value === 'string' && value.includes(':') && !value.includes('T')) {
        const [h, m] = value.split(':');
        const date = new Date();
        date.setHours(parseInt(h, 10));
        date.setMinutes(parseInt(m, 10));
        return format(date, 'h:mm a');
      }
      return format(new Date(value), 'h:mm a');
    }

    const date = parseInputDate(value);
    if (!date || isNaN(date.getTime())) return placeholder;
    if (mode === 'date') return format(date, 'MMM d, yyyy');
    return format(date, "MMM d, yyyy 'at' h:mm a");
  };

  return (
    <PickerWrapper>
      <InputTrigger onClick={() => setIsOpen(true)} $isOpen={isOpen}>
        <ValueText $hasValue={!!value}>{formatDisplayValue()}</ValueText>
        <IconWrapper>
          {value ? (
            <X size={18} onClick={handleClear} style={{ cursor: 'pointer' }} />
          ) : (
            mode === 'time' ? <Clock size={18} /> : <Calendar size={18} />
          )}
        </IconWrapper>
      </InputTrigger>

      <AnimatePresence>
        {isOpen && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          >
            <PickerContainer
              onClick={e => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              {mode !== 'time' && (
                <>
                  <Header>
                    <MonthNav>
                      <NavButton onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                        <ChevronLeft size={18} />
                      </NavButton>
                      <span>{format(currentMonth, 'MMMM yyyy')}</span>
                      <NavButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                        <ChevronRight size={18} />
                      </NavButton>
                    </MonthNav>
                  </Header>

                  <CalendarGrid>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                      <WeekDay key={d}>{d}</WeekDay>
                    ))}
                    {paddingDays.map((_, i) => <div key={`pad-${i}`} />)}
                    {days.map(day => (
                      <DayButton
                        key={day.toString()}
                        $isSameMonth={isSameMonth(day, currentMonth)}
                        $isToday={isToday(day)}
                        $isSelected={selectedDate && isSameDay(day, selectedDate)}
                        onClick={() => handleDateClick(day)}
                      >
                        {format(day, 'd')}
                      </DayButton>
                    ))}
                  </CalendarGrid>
                </>
              )}

              {mode !== 'date' && (
                <TimeSection style={mode === 'time' ? { borderTop: 'none', paddingTop: 0 } : {}}>
                  <TimeLabel>
                    <Clock size={16} />
                    Time
                  </TimeLabel>
                  <TimeInputWrapper>
                    <TimeInput
                      type="number"
                      value={time.hours.toString().padStart(2, '0')}
                      onChange={e => handleTimeChange('hours', e.target.value)}
                      min="0"
                      max="23"
                    />
                    <TimeSeparator>:</TimeSeparator>
                    <TimeInput
                      type="number"
                      value={time.minutes.toString().padStart(2, '0')}
                      onChange={e => handleTimeChange('minutes', e.target.value)}
                      min="0"
                      max="59"
                    />
                  </TimeInputWrapper>
                </TimeSection>
              )}

              <ActionButtons>
                <Button onClick={() => setIsOpen(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleConfirm}>Confirm</Button>
              </ActionButtons>
            </PickerContainer>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </PickerWrapper>
  );
};

export default GlassyDateTimePicker;
